import * as React from 'react'
import {useEffect, useState} from 'react'
import {DataObjectId, GTDStage, Hub, SandboxTree} from 'yougile-model'
import {TrackingEvent} from '@common/tracking-events'
import {StickerItem} from '@components/sticker/sticker-item'
import {SubtaskUtil} from '@extensions/subtask-util'
import {Tracking} from '../../../common/tracking'
import {Sync} from '../../client/sync'
import {SyncEvents} from '../../client/sync-events'
import {UserSettings} from '../../client/user-settings'
import {ChatUtils} from '../../extensions/chat-utils'
import {globals} from '../../extensions/globals-base'
import {PageService} from '../../extensions/page-service'
import {Sticker, systemStickerCtl} from '../../extensions/sticker/sticker'
import {Task} from '../../extensions/task'
export interface TaskItem {
  title: string
  id: DataObjectId
  location: DataObjectId
  by: DataObjectId
  companyId: DataObjectId
  timestamp: number
  assignedAt: number
  completedAt: number
  assigned: DataObjectId[]
  stage: GTDStage
  path: string
  stickers: Hub['stickers']
  archivedAt: number
  starred: boolean
  isPrivate: boolean
  parentIds: DataObjectId[]
  pathClick(): void
  order?: DataObjectId | 'top' | 'force-head'
  renderDeadline: () => JSX.Element | null
}

export function taskItemFromHub(box: SandboxTree, t: Hub): TaskItem

export function taskItemFromHub(
  box: SandboxTree,
  t: Hub | undefined
): TaskItem | undefined {
  if (!t) {
    return undefined
  }
  const stage = Task.getGTDStage(t)
  const assignedAt = t.data.assignedBy?.[globals.currentUserId]
    ? t.data.assignedBy[globals.currentUserId]
    : +(t.data.assignTime ?? 0) || t.timestamp
  const creator = Task.creator(t)
  return {
    title: t.title,
    id: t.id,
    location: t.location,
    companyId: t.companyId,
    timestamp: t.timestamp,
    by: creator?.id,
    stage,
    isPrivate: !!t.data.private,
    parentIds: SubtaskUtil.getParentTaskIds(t.id),
    assignedAt,
    assigned: Sticker.getUsers(t),
    archivedAt: t.complete,
    completedAt:
      t.data.gtd && stage === 'done' ? Task.getGTDLastChangeTime(t) : undefined,
    path: ChatUtils.getChatLocation(t),
    starred: UserSettings.isStarred(t),
    stickers: t.stickers,

    pathClick: () => {
      const column = box.get(t.location) as Hub
      if (column) {
        PageService.goToBoard(column.location, t.companyId)
        void Tracking.send(TrackingEvent.myTasksGoToBoard)
      }
    },
    renderDeadline: (): JSX.Element | null => {
      const ctl = systemStickerCtl.Deadline
      const sticker = ctl.getSticker(box)

      return (
        <StickerItem withMenu taskId={t.id} sticker={sticker} location="task" />
      )
    }
  }
}

export const useTaskFromHubWithEffect = (
  box: SandboxTree,
  t: Hub | undefined
): TaskItem => {
  const [task, setTask] = useState(taskItemFromHub(box, t))

  useEffect(() => {
    if (task?.id !== t?.id) {
      setTask(taskItemFromHub(box, t))
    }

    const id1 = SyncEvents.addListener(
      Sync.profileId,
      () => t?.companyId || globals.getCompanyId(),
      () => setTask(taskItemFromHub(box, t))
    )

    const id2 = t
      ? SyncEvents.addListener(t.companyId, t.id, () =>
          setTask(taskItemFromHub(box, t))
        )
      : undefined

    return () => {
      SyncEvents.removeListener(id1)
      id2 && SyncEvents.removeListener(id2)
    }
  }, [t?.id])
  return task
}

type HubStarred = {id: DataObjectId; companyId: DataObjectId; starred: boolean}

export function taskItemFromHubStarred(
  t: Hub | undefined
): HubStarred | undefined {
  if (!t) {
    return undefined
  }

  return {
    id: t.id,
    companyId: t.companyId,
    starred: UserSettings.isStarred(t)
  }
}

export const useTaskFromHubWithStarredEffect = (
  t: Hub | undefined
): HubStarred => {
  const [task, setTask] = React.useState(taskItemFromHubStarred(t))

  if (task?.id !== t?.id) {
    setTask(taskItemFromHubStarred(t))
  }

  React.useEffect(() => {
    const id1 = SyncEvents.addListener(
      Sync.profileId,
      () => t?.companyId || globals.getCompanyId(),
      () => setTask(taskItemFromHubStarred(t))
    )

    const id2 = t
      ? SyncEvents.addListener(t.companyId, t.id, () =>
          setTask(taskItemFromHubStarred(t))
        )
      : undefined
    return () => {
      SyncEvents.removeListener(id1)
      id2 && SyncEvents.removeListener(id2)
    }
  }, [t?.id])

  return task
}
